import React, { useState, useMemo } from 'react';
import Card from '../UI/Card';
import ProjectDetailsModal from '../UI/ProjectDetailsModal';
import { CONFIG } from '../../config';
import { Search, FolderOpen, ExternalLink, Image as ImageIcon } from 'lucide-react';

/**
 * Projects Section Component
 * 
 * Displays a grid of projects with filtering and search capabilities.
 * Manages state for the active filter, search query, and selected project for the modal.
 */
const Projects = () => {
    const [filter, setFilter] = useState('All');
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedProject, setSelectedProject] = useState(null);

    const categories = ['All', 'Web App', 'Mobile App', 'Enterprise', 'Desktop App'];

    // Memoized filter logic to efficiently update the specific project list based on user input
    const filteredProjects = useMemo(() => {
        return CONFIG.projects.filter(project => {
            const matchesCategory = filter === 'All' || project.category === filter;
            const matchesSearch = project.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                project.technologies.some(t => t.toLowerCase().includes(searchQuery.toLowerCase()));
            return matchesCategory && matchesSearch;
        });
    }, [filter, searchQuery]);

    return (
        <section className="py-12 md:py-20 px-4 sm:px-10 bg-slate-50 dark:bg-slate-900/50 transition-colors duration-300" id="projects-section">
            <div className="max-w-7xl mx-auto">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-12">
                    <div className="animate-in fade-in slide-in-from-left-4 duration-700">
                        <h2 className="text-3xl font-bold text-slate-900 dark:text-white mb-2">Featured Projects</h2>
                        <p className="text-slate-600 dark:text-slate-400 break-words pr-2">Explore my latest technical implementations.</p>
                    </div>

                    <div className="flex flex-col gap-4 animate-in fade-in slide-in-from-right-4 duration-700">
                        {/* Search */}
                        <div className="relative w-full">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500" size={18} />
                            <input
                                type="text"
                                placeholder="Search projects..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="w-full bg-white dark:bg-slate-800 border border-gray-200 dark:border-white/5 rounded-xl pl-10 pr-4 py-3 text-sm text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-purple-500/50 transition-all shadow-sm dark:shadow-none"
                            />
                        </div>

                        {/* Filter - Wrapped for mobile to prevent overflow/scrolling, buttons are big and clickable */}
                        <div className="flex flex-wrap gap-2">
                            {categories.map(cat => (
                                <button
                                    key={cat}
                                    onClick={() => setFilter(cat)}
                                    className={`flex-1 sm:flex-none px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 text-center whitespace-nowrap ${filter === cat
                                        ? 'bg-purple-600 text-white shadow-lg shadow-purple-500/25 scale-105'
                                        : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-gray-50 dark:hover:bg-white/5 border border-gray-200 dark:border-white/5 shadow-sm dark:shadow-none hover:scale-105'
                                        }`}
                                >
                                    {cat}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>

                {filteredProjects.length > 0 ? (
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {filteredProjects.map((project, index) => (
                            <Card
                                key={project.id}
                                className="group cursor-pointer flex flex-col h-full animate-in fade-in slide-in-from-bottom-8 fill-mode-both"
                                style={{ animationDelay: `${index * 100}ms` }}
                            >
                                <div onClick={() => setSelectedProject(project)} className="flex-1 flex flex-col h-full">
                                    <div className="flex justify-between items-start mb-4">
                                        <div className="p-3 bg-purple-100 dark:bg-purple-500/10 rounded-xl text-purple-600 dark:text-purple-400 group-hover:bg-purple-600 group-hover:text-white dark:group-hover:bg-purple-500 transition-colors duration-300">
                                            <FolderOpen size={24} className="group-hover:scale-110 transition-transform duration-300" />
                                        </div>
                                        <div className="flex gap-2">
                                            {project.images && project.images.length > 0 && (
                                                <span className="p-1.5 bg-gray-100 dark:bg-white/5 rounded-full text-slate-500 dark:text-slate-400 border border-gray-200 dark:border-white/5">
                                                    <ImageIcon size={14} />
                                                </span>
                                            )}
                                            {project.website && (
                                                <span className="p-1.5 bg-gray-100 dark:bg-white/5 rounded-full text-slate-500 dark:text-slate-400 border border-gray-200 dark:border-white/5">
                                                    <ExternalLink size={14} />
                                                </span>
                                            )}
                                            <span className="px-3 py-1 bg-gray-100 dark:bg-white/5 rounded-full text-xs font-medium text-slate-600 dark:text-slate-400 border border-gray-200 dark:border-white/5">
                                                {project.category}
                                            </span>
                                        </div>
                                    </div>

                                    <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-2 group-hover:text-purple-600 dark:group-hover:text-purple-400 transition-colors">
                                        {project.title}
                                    </h3>

                                    <p className="text-slate-600 dark:text-slate-400 text-sm leading-relaxed mb-6 line-clamp-3">
                                        {project.description}
                                    </p>

                                    <div className="mt-auto pt-6 border-t border-gray-100 dark:border-white/5">
                                        <div className="flex flex-wrap gap-2 text-xs text-slate-500 dark:text-slate-400">
                                            {project.technologies.slice(0, 3).map(tech => (
                                                <span key={tech} className="px-2 py-1 bg-gray-100 dark:bg-white/5 rounded-md">#{tech}</span>
                                            ))}
                                            {project.technologies.length > 3 && <span className="px-2 py-1">+{project.technologies.length - 3}</span>}
                                        </div>
                                    </div>
                                </div>
                            </Card>
                        ))}
                    </div>
                ) : (
                    <div className="text-center py-20 bg-white dark:bg-slate-800/30 rounded-3xl border border-dashed border-gray-300 dark:border-white/10 shadow-sm dark:shadow-none">
                        <div className="inline-flex p-4 bg-gray-100 dark:bg-slate-800 rounded-full text-slate-400 dark:text-slate-500 mb-4 animate-pulse">
                            <Search size={32} />
                        </div>
                        <p className="text-slate-600 dark:text-slate-300 font-medium">No projects found matching your criteria.</p>
                        <button
                            onClick={() => { setFilter('All'); setSearchQuery(''); }}
                            className="mt-4 text-purple-600 dark:text-purple-400 hover:text-purple-700 dark:hover:text-purple-300 text-sm font-medium transition-colors"
                        >
                            Clear filters
                        </button>
                    </div>
                )}
            </div>

            <ProjectDetailsModal
                project={selectedProject}
                onClose={() => setSelectedProject(null)}
            />
        </section>
    );
};

export default Projects;
